/**
 * Detail-route builders for design projects. The detail URLs carry the SEO slug
 * from the backend `_link` object plus a type-prefixed id segment:
 * realizace → /realizace/{slug}/r{id}, návrh → /navrhy/{slug}/n{id}
 * (see i18n.pages in nuxt.config.ts — every locale shares the same params).
 */

/** Minimal shape every link builder needs — DesignProject and the nested
 * `designProject` of a download-history row both satisfy it. */
export interface ProjectLinkSource {
  id: number
  type?: EnumObject | null
  _link?: ProjectLink | null
  seo?: { slug?: string }
}

/**
 * Route params for a project detail link. Slug prefers `_link.slug` (permanent
 * link), falls back to `seo.slug`, then a static placeholder — the id segment
 * alone identifies the project, the slug is cosmetic/SEO.
 */
export function projectDetailParams(project: ProjectLinkSource) {
  return {
    slug: project._link?.slug || project.seo?.slug || 'detail',
    id: project.id,
  }
}

/**
 * Full route location for a project detail. Without an explicit `routeName` the
 * route is derived from `type.value` (visualization → designs-id, else
 * projects-id) — pass the name where the caller's context decides it (e.g. the
 * favourites page groups).
 */
export function projectDetailRoute(project: ProjectLinkSource, routeName?: string) {
  return {
    name:
      routeName ?? (project.type?.value === 'visualization' ? 'designs-id' : 'projects-id'),
    params: projectDetailParams(project),
  }
}
